import * as React from "react";
import { useMemo } from "react";
import { useHistory, useLocation } from "react-router";
import { useIntl } from "react-intl";
import { Grid, Loader } from "semantic-ui-react";
import { get } from "lodash";

import { ConfirmationModal } from "@/components/Modals/ConfirmationModal";
import { SideBar } from "@/components/SideBar";
import { TitleSection } from "@/components/TitleSection";
import { Media, UploadProgressMap, VideoProjectRequest, VideoProjectSource } from "../../../interfaces";
import { AppContext } from "@/providers/AppContextProvider";
import { getSocialNetworkMessages, URLS } from "../../../utils";
import { VideoProjectServices } from "../../../services";

import { VideoProjectForm } from "./components/VideoProjectForm";
import { modalReducer } from "./reducer";

import { descriptors, VideoProjectPageType } from "./descriptors";
import { WhiteContainer } from "../../../styling/baseStyle";
import "./styles.scss";
import { ProjectFileUploader } from "../../../utils/projectFileUploader";
import { Dialog } from "@/components/ui";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { UploadProgressModal } from "../../../components/UploadProgressModal";
import { ContentPageServices } from "@/services/bedrock";

const VideoProjectFC: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ preloadMedia?: Media; scriptId?: number; contentPageId?: number }>();
  const { formatMessage } = useIntl();
  const { userContext } = React.useContext(AppContext);

  const [state, dispatch] = React.useReducer(modalReducer, {
    open: false,
    status: "ACTIVE",
    duplicated: false,
    action: "close",
  });

  const preloadMedia = get(location, "state.preloadMedia", undefined) as Media | undefined;
  const scriptId = get(location, "state.scriptId", undefined) as number | undefined;
  const contentPageId = get(location, "state.contentPageId", undefined) as number | undefined;

  // Properties
  const [loading, setLoading] = React.useState<boolean>(false);
  const [submittingProject, setSubmittingProject] = React.useState<boolean>(false);
  const [formErrorMessages, setFormErrorMessages] = React.useState<string[] | undefined>(undefined);
  const [uploadingFiles, setUploadingFiles] = React.useState(false);
  const [uploadProgressMap, setUploadProgressMap] = React.useState<UploadProgressMap>();
  const [isValid, setIsValid] = React.useState(true);
  const [videoProject, setVideoProject] = React.useState<any | undefined>({
    originalMediaGroup: preloadMedia ? { type: "VIDEO", media: [preloadMedia] } : undefined,
  });

  const playerId = useMemo(() => {
    return userContext?.player?.id;
  }, [userContext?.player?.id]);
  const coachId = useMemo(() => {
    return userContext?.subscriptions!![0].pricePlan.coachId;
  }, [userContext?.subscriptions]);

  const createVideoProject = async (): Promise<any> => {
    if (videoProject) {
      const {
        allowShortLink,
        allowComplianceDisclaimer,
        allowHashtags,
        isAutoPost,
        status,
        originalMediaGroup,
        withMessage,
        preferredPostDate,
        hashtags,
        initialMessages,
        preferences,
      } = videoProject;

      const snFiltered = Object.keys(videoProject.socialNetworkMessages).filter(
        key => videoProject.socialNetworkMessages[key]
      );
      const socialNetworkMessages = getSocialNetworkMessages(snFiltered);

      const uploader = new ProjectFileUploader("videoProject");
      const uploadMap: UploadProgressMap = {};
      originalMediaGroup.media.forEach((mediaObj: Media) => {
        uploadMap[mediaObj.id] = { progress: 0, task: undefined };
      });
      setUploadProgressMap(uploadMap);
      setUploadingFiles(true);
      originalMediaGroup.media = await uploader.prepareMedia(originalMediaGroup.media, (fileId, progress) => {
        setUploadProgressMap(map => {
          return { ...map, [fileId]: { progress, task: undefined } };
        });
      });
      setUploadingFiles(false);
      setSubmittingProject(true);

      const request: VideoProjectRequest = {
        playerId: playerId!!,
        coachId: coachId,
        socialNetworkMessages,
        messageToPost: withMessage,
        hashtags,
        originalMediaGroup,
        status,
        isAutoPost,
        preferredPostDate,
        allowShortLink,
        allowComplianceDisclaimer,
        initialMessages,
        allowPlayerHashtags: allowHashtags,
        videoScriptId: scriptId,
        preferences,
        source: VideoProjectSource.PLAYER_WEB,
      };

      VideoProjectServices.create(request)
        .then(async newProject => {
          if (contentPageId) {
            await ContentPageServices.updateContentPage(contentPageId, { videoProjectId: newProject.videoProjectId });
          }
          setSubmittingProject(false);
          toast.success("Project submitted successfully!");
          history.replace(URLS.player.videoProject.replace(":id", `${newProject.videoProjectId}`));
        })
        .catch(error => {
          setSubmittingProject(false);
          setFormErrorMessages([error.substring(0, 100)]);
        });
    }
  };

  const cancelAction = () => {
    dispatch({ type: "open_cancel", status: "ACTIVE" });
  };

  const confirmationModal = (): JSX.Element => {
    const modalTitle = formatMessage({
      ...descriptors[VideoProjectPageType.cancelAlertTitle],
    });
    return (
      <ConfirmationModal
        title={!state.duplicated ? modalTitle : undefined}
        message={""}
        openConfirmationModal={state.open}
        onClose={() => dispatch({ type: "close" })}
        okHandler={() => {
          dispatch({ type: "close" });
          history.push(URLS.player.videoProjects);
        }}
        rejectHandler={() => dispatch({ type: "close" })}
      />
    );
  };

  const customLinkSection = (): JSX.Element => (
    <Grid.Column width={7} floated="right" className="skip-link noMarginRight">
      <span className={"rightPadding"} data-elm-id={"videoProjectPageCancelBtn"} onClick={cancelAction}>
        {formatMessage({ ...descriptors[VideoProjectPageType.cancelBtn] })}
      </span>
    </Grid.Column>
  );

  const content = (): JSX.Element => {
    return (
      <Grid columns={1} className={"newProjectContentSection contentSection"}>
        {confirmationModal()}
        <UploadProgressModal open={uploadingFiles} progressMap={uploadProgressMap} />

        <Dialog open={submittingProject} onOpenChange={undefined} size="sm" disableEscapeDismissal={true}>
          <Dialog.Header>
            <Dialog.Title className="flex items-center gap-1.5 text-slate-800 text-sm font-semibold select-none">
              <Loader2 className="animate-spin size-4 text-indigo-600 shrink-0" />
              <span>Submitting Project</span>
            </Dialog.Title>
          </Dialog.Header>

          <Dialog.Content className="p-5 flex flex-col items-center justify-center gap-4 text-center">
            <div className="text-xs text-slate-600 font-medium">
              Please wait while we submit your video project changes. This may take a few moments.
            </div>
          </Dialog.Content>
        </Dialog>

        <TitleSection
          title={formatMessage({ ...descriptors[VideoProjectPageType.title] })}
          customLinkSection={customLinkSection()}
          customNextLabel={formatMessage({ ...descriptors[VideoProjectPageType.saveBtn] })}
          handleNextAction={() => createVideoProject()}
          disableButtons={uploadingFiles || submittingProject || !isValid}
          titleSize={8}
          buttonsSize={8}
          nextButtonSize={7}
          showLinkOption={true}
          showNextOption={true}
        />
        <Grid.Row columns={1} className={"formSection leftPadding"}>
          <Grid className={"infoSection"}>
            <Grid.Column width={16} className={"formContainer"}>
              {videoProject && (
                <VideoProjectForm
                  disabled={false}
                  modeEditable={false}
                  videoProject={videoProject}
                  handleValidation={setIsValid}
                  updatingHandler={setVideoProject}
                  pageErrors={formErrorMessages}
                  coachId={coachId!!}
                  playerId={`${playerId}`}
                />
              )}
            </Grid.Column>
          </Grid>
        </Grid.Row>
      </Grid>
    );
  };

  return (
    <WhiteContainer>
      <SideBar history={history} />
      {loading ? <Loader active size="large" /> : content()}
    </WhiteContainer>
  );
};

export const PlayerVideoProjectPage = VideoProjectFC;
